import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Column } from "react-table";
import { useTable } from "react-table";
import type { ISiteScheduleExceptionsRepository, ISiteVideoStreamsRepository } from "App/Modules/Repositories";
import { IssueSeverity } from "App/Modules/Models";
import type { IResponseWithLoadingAndTimestamp, IIssue } from "App/Modules/Models";
import {
    useMediaQuery,
    useTheme,
    Tabs,
    Tab,
    Typography,
    Checkbox,
    FormControlLabel,
    Collapse,
    Button,
} from "@material-ui/core";
import type { RefreshIntervalSeconds } from "App/ReactComponents/Shared";
import {
    DefaultTable,
    getRowId,
    getTableHooks,
    getUserProfile,
    hasUserAccessToSiteInformation,
    hasUserAccessToMonitoring,
    RefreshButton,
    SearchField,
} from "App/ReactComponents/Shared";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/pro-solid-svg-icons";
import type { CombinedSite } from "../../Models";
import { SitesListMenuItem } from "./SitesListItemMenu";
import { getSitesListUIColumns } from "./SitesListUIColumns";
import { SitesListUIDetailsPanel } from "./SitesListUIDetailsPanel";
import { defaultFilterTypes } from "../../Shared/Components/Table/filters/defaultFilterTypes";
import { useSitesListStyles } from "./SitesListStyles";
import "./_sitesListFilters.scss";

type Props = {
    sites: IResponseWithLoadingAndTimestamp<CombinedSite[]>;
    siteScheduleExceptionsRepository: ISiteScheduleExceptionsRepository;
    siteVideoStreamsRepository: ISiteVideoStreamsRepository;
    onRefresh?: (refreshInterval: RefreshIntervalSeconds) => void;
    updateIssues: (...issues: Array<Partial<IIssue>>) => void;
};

export const SitesListUI = (props: Props) => {
    const { t } = useTranslation();

    const profile = getUserProfile();
    const showSiteInformation = hasUserAccessToSiteInformation(profile);
    const showMonitoring = hasUserAccessToMonitoring(profile);

    const theme = useTheme();
    const smWidthUp = useMediaQuery(theme.breakpoints.up("sm"), {
        defaultMatches: true,
    });

    type SiteListMode = "overview" | "monitoring";

    const [sitesListMode, setSitesListMode] = React.useState<SiteListMode>("overview");

    const [siteMenuSite, setSiteMenuSite] = useState<CombinedSite>();
    const [isSiteMenuOpen, setIsSiteMenuOpen] = useState(false);
    const [siteMenuAnchorEl, setSiteMenuAnchorEl] = useState<Element>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showOnlyActiveSites, setShowOnlyActiveSites] = useState(true);
    const [showOnlyCriticalIssues, setShowOnlyCriticalIssues] = useState(false);
    const [showOnlyActiveIssues, setShowOnlyActiveIssues] = useState(false);
    const [showOnlyUnacknowledgedIssues, setShowOnlyUnacknowledgedIssues] = useState(false);
    const [filteredSites, setFilteredSites] = useState(props.sites?.data);

    const handleSiteMenuClicked = (site: CombinedSite, event: React.SyntheticEvent<any>) => {
        setSiteMenuSite(site);
        setSiteMenuAnchorEl(event.currentTarget);
        setIsSiteMenuOpen(true);
        event.stopPropagation();
    };

    const handleSiteMenuClose = () => {
        setIsSiteMenuOpen(false);
    };

    const handleAcknowledgeAll = (site: CombinedSite) => {
        if (!site || !site.numIssuesNew) {
            return;
        }

        const updatedIssues = site.issues
            .filter((i) => !i.isAcknowledged)
            .map((i) => ({
                id: i.id,
                isAcknowledged: true,
                acknowledgedBy: { id: "", name: profile.fullName },
            }));

        props.updateIssues(...updatedIssues);
    };

    const handleAcknowledgeAllResolved = (site: CombinedSite) => {
        if (!site || !site.numIssuesNew) {
            return;
        }

        const updatedIssues = site.issues
            .filter((i) => !i.isAcknowledged && !i.isActive)
            .map((i) => ({
                id: i.id,
                isAcknowledged: true,
                acknowledgedBy: { id: "", name: profile.fullName },
            }));

        props.updateIssues(...updatedIssues);
    };

    const columns = React.useMemo<Column<CombinedSite>[]>(
        () =>
            getSitesListUIColumns({
                handleSiteMenuClicked,
                showSiteInformation,
                showMonitoring,
                t,
                profile,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showSiteInformation, showMonitoring],
    );

    const filterTypes = defaultFilterTypes;

    const alwaysHiddenColumns = [
        "id",
        "friendlyId",
        "company.name",
        "partner.name",
        "labelNames",
        "siteState",
        "siteStateEnded",
    ];

    const hiddenColumnsPerMode = {
        overview: showSiteInformation ? ["issuesSummary", "numIssuesNew", "numIssuesActive", "dtLatestIssue"] : [],
        monitoring: showMonitoring ? ["numCameras", "eventsLastDay", "eventsLastWeek"] : [],
    };

    const extraHiddenColumnsPerModeSm = {
        overview: showSiteInformation ? ["numCameras"] : [],
        monitoring: showMonitoring ? ["dtLatestIssue"] : [],
    };

    const instance = useTable<CombinedSite>(
        {
            columns,
            data: filteredSites || [],
            filterTypes,
            getRowId,
            globalFilter: "fuzzyGlobalText",
            autoResetExpanded: false,
            autoResetHiddenColumns: false,
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetGlobalFilter: false,
            initialState: {
                hiddenColumns: [...alwaysHiddenColumns, ...hiddenColumnsPerMode[sitesListMode]],
                pageSize: 25,
                filters: [{ id: "siteStateIsEnded", value: "false" }],
            },
        },
        ...getTableHooks<CombinedSite>({
            expanded: true,
            pagination: true,
            globalFilter: true,
            rowState: true,
            filters: true,
        }),
    );

    const { setHiddenColumns } = instance;

    useEffect(() => {
        if (smWidthUp) {
            setHiddenColumns([...alwaysHiddenColumns, ...hiddenColumnsPerMode[sitesListMode]]);
        } else {
            setHiddenColumns([
                "avatar",
                ...alwaysHiddenColumns,
                ...hiddenColumnsPerMode[sitesListMode],
                ...extraHiddenColumnsPerModeSm[sitesListMode],
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smWidthUp, sitesListMode]);

    useEffect(() => {
        if (instance.state.globalFilter) {
            instance.setSortBy([]);
            instance.gotoPage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance.state.globalFilter, instance.setSortBy]);

    useEffect(() => {
        if (props.sites.data) {
            setFilteredSites(props.sites.data);
        }
    }, [props.sites.data]);

    const filterSites = () => {
        if (showOnlyCriticalIssues && showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredSites(
                props.sites.data.filter((s) =>
                    s.issues?.some((i) => i.severity === IssueSeverity.Error && i.isActive && !i.isAcknowledged),
                ),
            );
        } else if (showOnlyCriticalIssues && showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredSites(
                props.sites.data.filter((s) => s.issues?.some((i) => i.severity === IssueSeverity.Error && i.isActive)),
            );
        } else if (showOnlyCriticalIssues && !showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredSites(props.sites.data.filter((s) => s.issues?.some((i) => i.severity === IssueSeverity.Error)));
        } else if (!showOnlyCriticalIssues && showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredSites(props.sites.data.filter((s) => s.issues?.some((i) => i.isActive)));
        } else if (showOnlyCriticalIssues && !showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredSites(
                props.sites.data.filter((s) =>
                    s.issues?.some((i) => i.severity === IssueSeverity.Error && !i.isAcknowledged),
                ),
            );
        } else if (!showOnlyCriticalIssues && showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredSites(props.sites.data.filter((s) => s.issues?.some((i) => i.isActive && !i.isAcknowledged)));
        } else if (!showOnlyCriticalIssues && !showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredSites(props.sites.data.filter((s) => s.issues?.some((i) => !i.isAcknowledged)));
        } else {
            setFilteredSites(props.sites.data);
        }
    };

    useEffect(() => {
        filterSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnlyCriticalIssues, showOnlyActiveIssues, showOnlyUnacknowledgedIssues]);

    const handleSiteListModeChange = (event: React.ChangeEvent<object>, newValue: SiteListMode) => {
        setSitesListMode(newValue);
    };

    const handleShowOnlyActiveChange = (newValue: boolean) => {
        setShowOnlyActiveSites(newValue);
        const col = instance.allColumns.find((c) => c.id === "siteStateIsEnded");
        if (newValue) {
            col.setFilter("false");
        } else {
            col.setFilter("");
        }
    };

    const handleShowOnlyCriticalIssuesChange = (value: boolean) => {
        setShowOnlyCriticalIssues(value);
    };

    const handleShowOnlyActiveIssuesChange = (value: boolean) => {
        setShowOnlyActiveIssues(value);
    };

    const handleShowOnlyUnacknowledgedIssuesChange = (value: boolean) => {
        setShowOnlyUnacknowledgedIssues(value);
    };

    const { data: sitesData, ...sitesLoadingState } = props.sites;

    const classes = useSitesListStyles();

    return (
        <>
            <div className={classes.sitesListHeader}>
                <div className={classes.sitesListHeaderPrimaryRow}>
                    <Typography variant="h3">{t("Entities.Site.Sites")}</Typography>
                    <RefreshButton {...sitesLoadingState} onRefresh={(interval) => props.onRefresh(interval)} />
                </div>
                <SearchField globalFilter={instance.state.globalFilter} setGlobalFilter={instance.setGlobalFilter} />
                <div className="sites-list-filters">
                    <Button
                        variant="text"
                        size="small"
                        color="default"
                        className="sites-list-filters__filter-btn"
                        onClick={() => setShowFilters(!showFilters)}
                        startIcon={<FontAwesomeIcon icon={faFilter} className="sites-list-filters__button-icon" />}
                    >
                        {t("Gui.Manage.SitesListFilter.ShowOnly")}
                    </Button>
                    {!showFilters &&
                    (showOnlyCriticalIssues || showOnlyActiveIssues || showOnlyUnacknowledgedIssues) ? (
                        <span className="sites-list-filters__info-text">
                            {t("Gui.Manage.SitesListFilter.FilterActive")}
                        </span>
                    ) : undefined}
                    <Collapse in={showFilters}>
                        <FormControlLabel
                            className={classes.sitesList__showEndedSites}
                            control={
                                <Checkbox
                                    checked={showOnlyActiveSites}
                                    onChange={(_, checked) => handleShowOnlyActiveChange(checked)}
                                    color="primary"
                                />
                            }
                            label={t("Gui.Manage.SitesListFilter.ActiveSites")}
                        />
                        <FormControlLabel
                            className={classes.sitesList__showEndedSites}
                            control={
                                <Checkbox
                                    checked={showOnlyCriticalIssues}
                                    onChange={(_, checked) => handleShowOnlyCriticalIssuesChange(checked)}
                                    color="primary"
                                />
                            }
                            label={t("Gui.Manage.SitesListFilter.CriticalIssues")}
                        />
                        <FormControlLabel
                            className={classes.sitesList__showEndedSites}
                            control={
                                <Checkbox
                                    checked={showOnlyActiveIssues}
                                    onChange={(_, checked) => handleShowOnlyActiveIssuesChange(checked)}
                                    color="primary"
                                />
                            }
                            label={t("Gui.Manage.SitesListFilter.ActiveIssues")}
                        />
                        <FormControlLabel
                            className={classes.sitesList__showEndedSites}
                            control={
                                <Checkbox
                                    checked={showOnlyUnacknowledgedIssues}
                                    onChange={(_, checked) => handleShowOnlyUnacknowledgedIssuesChange(checked)}
                                    color="primary"
                                />
                            }
                            label={t("Gui.Manage.SitesListFilter.UnacknowledgedIssues")}
                        />
                    </Collapse>
                </div>
            </div>
            <Tabs value={sitesListMode} onChange={handleSiteListModeChange}>
                <Tab label={t("Gui.Common.Overview")} value="overview" />
                {showMonitoring && <Tab label={t("Gui.Common.Monitoring")} value="monitoring" />}
            </Tabs>
            <DefaultTable
                instance={instance}
                loadingState={props.sites}
                expandDetailsTemplate={
                    !(showSiteInformation || showMonitoring)
                        ? undefined
                        : (row) => (
                              <SitesListUIDetailsPanel
                                  row={row}
                                  sitesListMode={sitesListMode}
                                  updateIssues={props.updateIssues}
                                  siteVideoStreamsRepository={props.siteVideoStreamsRepository}
                                  sitesLoadingState={sitesLoadingState}
                                  showOnlyCriticalIssues={showOnlyCriticalIssues}
                                  showOnlyActiveIssues={showOnlyActiveIssues}
                                  showOnlyUnacknowledgedIssues={showOnlyUnacknowledgedIssues}
                              />
                          )
                }
                caption={sitesListMode === "monitoring" ? t("Entities.Issues.MaxNumberOfFetchedIssues") : undefined}
            />
            <SitesListMenuItem
                isOpen={isSiteMenuOpen}
                site={siteMenuSite}
                onAcknowledgeAll={() => handleAcknowledgeAll(siteMenuSite)}
                onAcknowledgeAllResolved={() => handleAcknowledgeAllResolved(siteMenuSite)}
                showAcknowledgeAll={showMonitoring}
                siteScheduleExceptionsRepository={props.siteScheduleExceptionsRepository}
                anchorEl={siteMenuAnchorEl}
                onClose={() => handleSiteMenuClose()}
            />
        </>
    );
};
